import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { COLORS } from "../utils/colors";

interface EarlyAccessPageProps {
  // App info
  appName: string;
  appTagline: string;
  appDescription: string;
  
  // Features
  features: string[];
  
  // API endpoint
  apiEndpoint: string;
  
  // Customization
  accentColor?: string;
  initialSignupCount?: number;
  targetSignupCount?: number;
}

const EarlyAccessPage = ({
  appName,
  appTagline,
  appDescription,
  features,
  apiEndpoint,
  accentColor = COLORS.primary,
  initialSignupCount = 2290,
  targetSignupCount = 2317,
}: EarlyAccessPageProps) => {
  const [signupCount, setSignupCount] = useState(initialSignupCount);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    let count = initialSignupCount;
    const target = targetSignupCount;
    const interval = setInterval(() => {
      count += 1;
      setSignupCount(count);
      if (count >= target) clearInterval(interval);
    }, 60);
    return () => clearInterval(interval);
  }, [initialSignupCount, targetSignupCount]);

  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch(apiEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setStatus("success");
        setEmail("");
      } else {
        setStatus("error");
      }
    } catch (error) {
      console.error("Error submitting email:", error);
      setStatus("error");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: COLORS.light,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 8,
        px: 2,
      }}
    >
      <Container
        maxWidth="sm"
        sx={{
          textAlign: "center",
          bgcolor: "white",
          p: { xs: 3, sm: 5 },
          borderRadius: 4,
          boxShadow: 3,
          width: "100%",
        }}
      >
        <Typography
          variant={isMobile ? "h5" : "h4"}
          fontWeight="bold"
          mb={1}
          sx={{ color: COLORS.dark }}
        >
          {appName}: {appTagline}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" mb={3}>
          {appDescription}
        </Typography>

        <Box sx={{ textAlign: "left", mb: 3 }}>
          {features.map((feature, index) => (
            <Typography key={index}>✅ {feature}</Typography>
          ))}
        </Box>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="outlined"
            placeholder="Enter your email to get early access"
            required
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <FavoriteBorderIcon sx={{ color: accentColor }} />
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            sx={{
              bgcolor: accentColor,
              ":hover": { bgcolor: accentColor, opacity: 0.9 },
              mb: 2,
              fontWeight: 600,
            }}
          >
            Join the Somewords Waitlist
          </Button>

          {status === "success" && (
            <Typography color="success.main">
              You're on the Somewords waitlist! 🎉 We'll notify you when we launch.
            </Typography>
          )}
          {status === "error" && (
            <Typography color="error">
              Something went wrong. Try again.
            </Typography>
          )}
        </form>

        <Typography variant="body2" color="text.secondary" mt={2}>
          🚀 {signupCount.toLocaleString()} people waiting to capture their special moments
        </Typography>
      </Container>
    </Box>
  );
};

export default EarlyAccessPage;
